import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { cn } from "../../../lib/utils";
import { ComponentProps, ReactNode, forwardRef } from "react";

const toneStyles = {
  zinc950: {
    contained: "bg-innoveraZinc-950 text-white",
    outlined: "border-innoveraZinc-950 text-innoveraZinc-950",
    text: "text-innoveraZinc-950 hover:bg-innoveraZinc-100 active:bg-innoveraZinc-100"
  },
  orange400: {
    contained: "bg-innoveraOrange-400 text-white",
    outlined:
      "border-innoveraOrange-400 text-innoveraOrange-400 hover:border-innoveraOrange-300 active:border-innoveraOrange-300",
    text: "text-innoveraOrange-400 hover:bg-innoveraOrange-100 active:bg-innoveraOrange-100"
  },
  zinc150: {
    contained: "bg-innoveraZinc-150 text-innoveraZinc-950 hover:bg-innoveraZinc-200 active:bg-innoveraZinc-200",
    outlined:
      "border-innoveraZinc-150 text-innoveraZinc-950 hover:border-innoveraZinc-200 active:border-innoveraZinc-200",
    text: "text-innoveraZinc-950 hover:bg-innoveraZinc-150 active:bg-innoveraZinc-150"
  },
  zinc100: {
    contained: "bg-innoveraZinc-100 text-innoveraZinc-950 hover:bg-innoveraZinc-150 active:bg-innoveraZinc-150",
    outlined:
      "border-innoveraZinc-100 text-innoveraZinc-950 hover:border-innoveraZinc-150 active:border-innoveraZinc-150",
    text: "text-innoveraZinc-950 hover:bg-innoveraZinc-100 active:bg-innoveraZinc-100"
  },
  green500: {
    contained: "bg-innoveraGreen-500 text-white hover:bg-innoveraGreen-700 active:bg-innoveraGreen-700",
    outlined:
      "border-innoveraGreen-500 text-innoveraGreen-500 hover:border-innoveraGreen-700 active:border-innoveraGreen-700",
    text: "text-innoveraGreen-500 hover:bg-innoveraGreen-50 active:bg-innoveraGreen-50"
  },
  red400: {
    contained: "bg-innoveraRed-400 text-white",
    outlined: "border-innoveraRed-400 text-innoveraRed-400",
    text: "text-innoveraRed-400 hover:bg-innoveraRed-50 active:bg-innoveraRed-50"
  },
  white: {
    contained: "bg-innoveraGeneral-white text-innoveraZinc-950 hover:bg-innoveraZinc-100 active:bg-innoveraZinc-100",
    outlined:
      "border-innoveraGeneral-white text-innoveraZinc-950 hover:border-innoveraZinc-100 active:border-innoveraZinc-100",
    text: "text-innoveraZinc-950 hover:bg-innoveraZinc-100 active:bg-innoveraZinc-100"
  }
} as const;

type Appearance = "contained" | "outlined" | "text";
type ColorKey = keyof typeof toneStyles;

const compoundVariants: {
  appearance: Appearance;
  color: ColorKey;
  class: string;
}[] = Object.entries(toneStyles).flatMap(([color, styleObj]) =>
  (Object.entries(styleObj) as [Appearance, string][]).map(([appearance, cls]) => ({
    appearance,
    color: color as ColorKey,
    class: cls
  }))
);

const appButtonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-[color,box-shadow,background,border-color] disabled:pointer-events-none disabled:text-innoveraZinc-400 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0 outline-hidden focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      appearance: {
        contained: "disabled:bg-innoveraZinc-100 disabled:border-none",
        outlined: "border bg-transparent disabled:bg-transparent disabled:border-innoveraZinc-150",
        text: "bg-transparent disabled:bg-transparent"
      },
      color: {
        zinc950: "",
        orange400: "",
        zinc100: "",
        zinc150: "",
        green500: "",
        red400: "",
        white: ""
      },
      size: {
        default: "h-8 min-h-8 px-3 rounded-lg text-[14px] leading-[13px] has-[>svg]:px-2.5",
        sm: "h-6 px-2 rounded-md text-[12px] leading-[12px] gap-1.5 has-[>svg]:px-1.5",
        icon: "w-8 h-8 p-2 rounded-lg [&_svg]:size-4",
        iconSm: "w-6 h-6 p-1.5 rounded-md [&_svg]:size-3"
      }
    },
    compoundVariants,
    defaultVariants: {
      appearance: "contained",
      color: "zinc950",
      size: "default"
    }
  }
);

export type TAppButtonProps = ComponentProps<"button"> &
  VariantProps<typeof appButtonVariants> & {
    asChild?: boolean;
    isLoading?: boolean;
    loadingText?: string;
    startAdornment?: ReactNode;
    endAdornment?: ReactNode;
  };

const AppButton = forwardRef<HTMLButtonElement, TAppButtonProps>((props, ref) => {
  const {
    asChild,
    appearance,
    color,
    size,
    isLoading = false,
    loadingText = "Loading...",
    startAdornment,
    endAdornment,
    className,
    type,
    disabled,
    ...otherProps
  } = props;

  const Comp = asChild ? Slot : "button";
  const isIconSize = size?.includes("icon");

  return (
    <Comp
      ref={ref}
      data-slot="button"
      className={cn(appButtonVariants({ appearance, color, size }), className)}
      {...otherProps}
      disabled={isLoading || disabled}
      type={type || "button"}
    >
      {isLoading ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          {!isIconSize && loadingText}
        </>
      ) : (
        <>
          {startAdornment}
          {props.children}
          {endAdornment}
        </>
      )}
    </Comp>
  );
});

AppButton.displayName = "AppButton";

export { AppButton, appButtonVariants };
